import type { UserFeature } from '~/lib/bff';
import type { User } from '~/modules/auth';
import type { HasPermissionFunction } from '~/modules/auth';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

export enum NavigationPermissionKeys {
  RELEASE_CLINIC = 'RELEASE_CLINIC',
  VIDEO_MANAGEMENT = 'VIDEO_MANAGEMENT',
  REPORTS = 'REPORTS',
  CREATE_RELEASE = 'CREATE_RELEASE',
  COOP_SETTINGS = 'COOP_SETTINGS',
  SETTINGS = 'SETTINGS',
  WMG_RELEASE_MANAGEMENT = 'WMG_RELEASE_MANAGEMENT',
  WMG_LINEUP = 'WMG_LINEUP',
}

export const useHasSettingsEnabled = (features: UserFeature[] = []) => {
  return features.some(
    (feature) =>
      feature.enabled && feature.systemPermission === 'VIEW_NEW_PERMISSIONS_UI',
  );
};

/**
 * Returns true when the user should be kept out of the legacy V1 / coop settings
 * UI (both the sidebar gear icon and direct URL visits to `/platform/settings`).
 *
 * Triggered by either of these user features being enabled (returned from
 * `/api/user-info`):
 * - `NEW_PERMISSIONS`: the V2 permissions enforcement flag.
 * - `ONLY_VIEW_NEW_PERMISSIONS_UI`: BFF-computed flag that opts a user into
 *   the new permissions UI exclusively (see `bff/only_view_new_permissions_ui.py`).
 */
export const useShouldHideLegacySettings = (features: UserFeature[] = []) => {
  return features.some(
    (feature) =>
      feature.enabled &&
      (feature.systemPermission === 'NEW_PERMISSIONS' ||
        feature.systemPermission === 'ONLY_VIEW_NEW_PERMISSIONS_UI'),
  );
};

export const useHasDdexConsoleAccess = (
  hasPermission: HasPermissionFunction,
) => {
  const [isDdexConsoleEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerDdexConsole,
  ]);

  return isDdexConsoleEnabled && hasPermission('DDEX.VIEW');
};

export const NAVIGATION_PERMISSIONS: Record<
  NavigationPermissionKeys,
  (
    hasPermission: HasPermissionFunction,
    context?: Record<string, unknown>,
  ) => boolean
> = {
  [NavigationPermissionKeys.RELEASE_CLINIC]: (hasPermission) =>
    hasPermission('RELEASE_CLINIC.VIEW_INTEGRATION'),

  [NavigationPermissionKeys.VIDEO_MANAGEMENT]: (hasPermission) => {
    return (
      // @TODO PERMS-659 Remove the V1 'VIDEO_CLAIMING.VIEW' permission check when V1 permissions are deprecated.
      hasPermission('VIDEO_CLAIMING.VIEW') || hasPermission('VIDEO_CLAIM.VIEW')
    );
  },

  [NavigationPermissionKeys.REPORTS]: (hasPermission) =>
    hasPermission('REPORT.VIEW'),
  [NavigationPermissionKeys.CREATE_RELEASE]: (hasPermission) =>
    hasPermission('RELEASE.CREATE'),
  [NavigationPermissionKeys.COOP_SETTINGS]: (hasPermission, context) => {
    const {
      user: { features },
    } = (context as Record<'user', User>) || { user: { features: [] } };

    return (
      hasPermission('PLATFORM.SETTINGS') &&
      !useShouldHideLegacySettings(features)
    );
  },

  [NavigationPermissionKeys.SETTINGS]: (hasPermission, context) => {
    const {
      user: { features },
    } = (context as Record<'user', User>) || { user: { features: [] } };

    return useHasSettingsEnabled(features) && hasPermission('USER.VIEW');
  },
  [NavigationPermissionKeys.WMG_RELEASE_MANAGEMENT]: () => {
    const [isWmgReleaseManagementEnabled] = useFeatureFlags([
      FEATURE_FLAGS.WmgReleaseManagement,
    ]);

    return isWmgReleaseManagementEnabled;
  },
  // @TODO DPAM-1230: combine with hasPermission once the page-access permission lands.
  [NavigationPermissionKeys.WMG_LINEUP]: () => {
    const [isWmgLineupEnabled] = useFeatureFlags([FEATURE_FLAGS.WmgLineup]);

    return isWmgLineupEnabled;
  },
};
